import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { SupportTicket, SupportTicketConversationItem, SupportTicketStatus } from 'src/app/models/support-ticket.model';
import { AuthService } from 'src/app/services/auth.service';
import { SupportTicketService } from 'src/app/services/support-ticket.service';
import { environment } from 'src/environments/environment';
import {
  SUPPORT_TICKET_MAX_DESCRIPTION_LENGTH,
  SUPPORT_TICKET_MAX_FILE_SIZE,
} from './support-ticket.constants';

interface TicketAttachmentPill {
  name: string;
  size: string;
  url?: string | null;
}

interface TicketConversationMessage {
  id: string;
  senderName: string;
  senderEmail?: string;
  role: 'user' | 'admin' | 'resolved';
  timestamp: string;
  text: string;
  attachment?: TicketAttachmentPill | null;
}

@Component({
  selector: 'app-support-ticket-detail',
  templateUrl: './support-ticket-detail.component.html',
  styleUrls: ['./support-ticket-detail.component.scss'],
})
export class SupportTicketDetailComponent implements OnInit, OnDestroy {
  private readonly defaultLoadErrorMessage = 'Unable to load support ticket details right now.';
  private readonly notFoundErrorMessage = 'Support ticket not found.';

  readonly maxReplyLength = SUPPORT_TICKET_MAX_DESCRIPTION_LENGTH;
  readonly maxFileSize = SUPPORT_TICKET_MAX_FILE_SIZE;
  ticket: SupportTicket | null = null;
  loading = true;
  hasError = false;
  errorMessage = this.defaultLoadErrorMessage;
  replyError = '';
  isReplySubmitting = false;
  isConversationExpanded = true;
  draftReply = '';
  selectedReplyFile: File | null = null;
  conversationMessages: TicketConversationMessage[] = [];
  customerName = '';
  resolvedByName = 'Eartheye Support';
  resolvedByEmail = 'support@eartheye.space';
  resolutionNotes = '';

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly authService: AuthService,
    private readonly supportTicketService: SupportTicketService
  ) {}

  ngOnInit(): void {
    this.customerName = this.getDefaultCustomerName();

    this.route.paramMap.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      const ticketId = params.get('id');
      if (!ticketId) {
        this.hasError = true;
        this.loading = false;
        return;
      }

      this.loadTicket(ticketId);
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  back(): void {
    this.router.navigate(['/support-tickets']);
  }

  toggleConversation(): void {
    this.isConversationExpanded = !this.isConversationExpanded;
  }

  viewAttachment(): void {
    const attachmentUrl = this.resolveAttachmentUrl();
    if (attachmentUrl) {
      window.open(attachmentUrl, '_blank');
    }
  }

  viewConversationAttachment(message: TicketConversationMessage): void {
    const attachmentUrl = message.attachment?.url;
    if (attachmentUrl) {
      window.open(attachmentUrl, '_blank');
    }
  }

  triggerReplyAttachment(fileInput: HTMLInputElement): void {
    fileInput.click();
  }

  onReplyFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;

    if (input.files?.length) {
      void this.setReplyFile(input.files[0], input);
      return;
    }

    this.selectedReplyFile = null;
  }

  clearReplyAttachment(fileInput?: HTMLInputElement): void {
    this.selectedReplyFile = null;
    this.replyError = '';

    if (fileInput) {
      fileInput.value = '';
    }
  }

  hasValidReplyContent(): boolean {
    return this.draftReply.trim().length > 0;
  }

  sendReply(fileInput?: HTMLInputElement): void {
    const message = this.draftReply;
    const ticketId = this.ticket?.id;

    if (this.isReplyDisabled() || !this.hasValidReplyContent() || !ticketId || this.isReplySubmitting) {
      return;
    }

    if (message.length > this.maxReplyLength) {
      this.replyError = `Reply cannot exceed ${this.maxReplyLength} characters.`;
      return;
    }

    this.replyError = '';
    this.isReplySubmitting = true;

    this.supportTicketService
      .submitUserReply(ticketId, message, this.selectedReplyFile, false)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (ticket) => {
          this.draftReply = '';
          this.clearReplyAttachment(fileInput);
          this.refreshTicketAfterReply(ticketId, this.mergeReplyTicketWithCurrent(ticket));
        },
        error: () => {
          this.replyError = 'Unable to send your reply right now. Please try again.';
          this.isReplySubmitting = false;
        },
      });
  }

  getStatusClass(status: string): string {
    switch (status) {
      case SupportTicketStatus.WorkInProgress:
        return 'status-pill in-progress';
      case SupportTicketStatus.Completed:
        return 'status-pill completed';
      default:
        return 'status-pill received';
    }
  }

  hasAttachment(): boolean {
    return !!this.resolveAttachmentUrl();
  }

  get replyLength(): number {
    return this.draftReply.length;
  }

  getAttachmentName(): string {
    return this.ticket?.attachment?.fileName || this.ticket?.attachment?.name || 'support-document.pdf';
  }

  getAttachmentSize(): string {
    const size = this.ticket?.attachment?.size;

    if (!size) {
      return 'PDF';
    }

    return this.formatFileSize(size);
  }

  getConversationToggleIcon(): string {
    return this.isConversationExpanded ? 'expand_less' : 'expand_more';
  }

  getConversationToggleLabel(): string {
    return this.isConversationExpanded ? 'Collapse conversation' : 'Expand conversation';
  }

  getCompletionDateLabel(): string {
    return this.ticket?.completedAt ? 'Completed Date (UTC)' : 'Expected Completion Date (UTC)';
  }

  getCompletionDateValue(): string | null {
    return this.ticket?.completedAt || this.ticket?.expectedCompletionDate || null;
  }

  isReplyDisabled(): boolean {
    return this.ticket?.status === SupportTicketStatus.Completed;
  }

  getReplyDisabledMessage(): string {
    return 'Cannot reply to a completed ticket';
  }

  getConversationClass(role: TicketConversationMessage['role']): string {
    switch (role) {
      case 'admin':
        return 'conversation-message admin-message';
      case 'resolved':
        return 'conversation-message resolved-message';
      default:
        return 'conversation-message user-message';
    }
  }

  private async setReplyFile(file: File, fileInput?: HTMLInputElement): Promise<void> {
    this.replyError = '';

    if (!(await this.isPdfFile(file))) {
      this.selectedReplyFile = null;
      this.replyError = 'Only PDF files are allowed.';
      this.resetFileInput(fileInput);
      return;
    }

    if (file.size > this.maxFileSize) {
      this.selectedReplyFile = null;
      this.replyError = 'File size must be 5 MB or less.';
      this.resetFileInput(fileInput);
      return;
    }

    this.selectedReplyFile = file;
  }

  private async isPdfFile(file: File): Promise<boolean> {
    const fileName = file.name.toLowerCase();
    const hasPdfNameOrMime = file.type === 'application/pdf' || fileName.endsWith('.pdf');

    if (!hasPdfNameOrMime) {
      return false;
    }

    try {
      const headerBuffer = await file.slice(0, 5).arrayBuffer();
      const headerBytes = new Uint8Array(headerBuffer);

      return headerBytes.length === 5
        && headerBytes[0] === 0x25
        && headerBytes[1] === 0x50
        && headerBytes[2] === 0x44
        && headerBytes[3] === 0x46
        && headerBytes[4] === 0x2d;
    } catch {
      return false;
    }
  }

  private resetFileInput(fileInput?: HTMLInputElement): void {
    if (fileInput) {
      fileInput.value = '';
    }
  }

  getAvatarClass(role: TicketConversationMessage['role']): string {
    switch (role) {
      case 'admin':
        return 'message-avatar avatar-admin';
      case 'resolved':
        return 'message-avatar avatar-resolved';
      default:
        return 'message-avatar avatar-user';
    }
  }

  getInitials(name: string): string {
    return name
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part.charAt(0).toUpperCase())
      .join('');
  }

  private loadTicket(ticketId: string): void {
    this.loading = true;
    this.hasError = false;
    this.errorMessage = this.defaultLoadErrorMessage;
    this.ticket = null;

    this.supportTicketService
      .getTicket(ticketId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (ticket) => {
          if (this.isMissingTicket(ticket)) {
            this.loading = false;
            this.hasError = true;
            this.errorMessage = this.notFoundErrorMessage;
            return;
          }

          this.ticket = ticket;
          this.populateTicketDetails(ticket);
          this.loading = false;
        },
        error: (error) => {
          this.loading = false;
          this.hasError = true;
          this.errorMessage = this.isNotFoundError(error)
            ? this.notFoundErrorMessage
            : this.defaultLoadErrorMessage;
        },
      });
  }

  private isNotFoundError(error: unknown): boolean {
    return error instanceof HttpErrorResponse && error.status === 404;
  }

  private isMissingTicket(ticket: SupportTicket | null | undefined): boolean {
    if (!ticket) {
      return true;
    }

    const ticketId = String(ticket.id || '').trim();
    const ticketNumber = String(ticket.ticketNo || '').trim();

    return !ticketId && (!ticketNumber || ticketNumber === '-');
  }

  private populateTicketDetails(ticket: SupportTicket): void {
    this.customerName =
      ticket.customerName?.trim() || ticket.requesterName?.trim() || this.getDefaultCustomerName();

    const latestAdminMessage = this.getLatestAdminConversation(ticket.conversation || []);
    this.resolutionNotes = this.buildResolutionNotes(ticket);
    this.resolvedByName = this.resolutionNotes && latestAdminMessage ? this.getSenderName(latestAdminMessage) : '';
    this.resolvedByEmail = this.resolutionNotes ? latestAdminMessage?.senderId || '' : '';
    this.conversationMessages = this.buildConversationMessages(ticket);
  }

  private buildResolutionNotes(ticket: SupportTicket): string {
    return ticket.lastAdminReply?.trim() || '';
  }

  private buildConversationMessages(ticket: SupportTicket): TicketConversationMessage[] {
    return (ticket.conversation || [])
      .filter((message) => !!message?.message)
      .map((message) => ({
        id: message.id,
        senderName: this.getSenderName(message),
        senderEmail: message.senderId,
        role: this.getConversationRole(message, ticket),
        timestamp: message.createdAt || ticket.completedAt || ticket.createdOn || ticket.createdAt || new Date().toISOString(),
        text: message.message,
        attachment: this.buildConversationAttachment(message.attachment),
      }));
  }

  private formatFileSize(sizeInBytes: number): string {
    if (sizeInBytes < 1024) {
      return `${sizeInBytes} B`;
    }

    if (sizeInBytes < 1024 * 1024) {
      return `${Math.round(sizeInBytes / 1024)} KB`;
    }

    return `${(sizeInBytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  private resolveAttachmentUrl(): string | null {
    const attachment = this.ticket?.attachment;
    return this.getViewableAttachmentUrl(attachment || null);
  }

  private buildConversationAttachment(attachment: SupportTicket['attachment']): TicketAttachmentPill | null {
    if (!attachment) {
      return null;
    }

    return {
      name: attachment.fileName || attachment.name || 'attachment.pdf',
      size: attachment.size ? this.formatFileSize(attachment.size) : 'PDF',
      url: this.getViewableAttachmentUrl(attachment),
    };
  }

  private getViewableAttachmentUrl(attachment: SupportTicket['attachment'] | null): string | null {
    const directUrl = attachment?.downloadUrl || attachment?.fileUrl || attachment?.url || null;
    const rawPath = attachment?.path || null;

    if (!directUrl) {
      if (rawPath && /^(https?:\/\/|blob:|data:)/i.test(rawPath)) {
        return rawPath;
      }

      return null;
    }

    if (/^(https?:\/\/|blob:|data:)/i.test(directUrl)) {
      return directUrl;
    }

    return `${environment.eeTaskServer}/${directUrl.replace(/^\//, '')}`;
  }

  private refreshTicketAfterReply(ticketId: string, fallbackTicket: SupportTicket): void {
    this.supportTicketService
      .getTicket(ticketId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (ticket) => {
          this.ticket = ticket;
          this.populateTicketDetails(ticket);
          this.isReplySubmitting = false;
        },
        error: () => {
          this.ticket = fallbackTicket;
          this.populateTicketDetails(fallbackTicket);
          this.isReplySubmitting = false;
        },
      });
  }

  private mergeReplyTicketWithCurrent(ticket: SupportTicket): SupportTicket {
    if (!this.ticket) {
      return ticket;
    }

    return {
      ...ticket,
      attachment: ticket.attachment || this.ticket.attachment,
    };
  }

  private getLatestAdminConversation(conversation: SupportTicketConversationItem[]): SupportTicketConversationItem | null {
    const adminMessages = conversation.filter((message) => String(message.senderType).toLowerCase() === 'admin');

    return adminMessages.length ? adminMessages[adminMessages.length - 1] : null;
  }

  private getSenderName(message: SupportTicketConversationItem): string {
    if (String(message.senderType).toLowerCase() !== 'admin') {
      return this.customerName;
    }

    return 'Eartheye Support';
  }

  private getConversationRole(
    message: SupportTicketConversationItem,
    ticket: SupportTicket
  ): TicketConversationMessage['role'] {
    const isAdmin = String(message.senderType).toLowerCase() === 'admin';
    return isAdmin ? 'admin' : 'user';
  }

  private getDefaultCustomerName(): string {
    const currentUser: any = this.authService.userValue || this.authService.getCurrentUser() || {};
    return currentUser?.name?.trim() || currentUser?.displayname?.trim() || currentUser?.displayName?.trim() || 'Customer';
  }
}